import { MeasureCoreEngine } from '../../manager/computer/measure.core.engine';
import { FeiVO } from './fei.vo';
import { MeasureVO } from './measure.vo';
import { MeasMode, MeasModeItem } from '../../manager/computer/measure.mode.obj';
import { Model } from '../../model/model';

/**
 * encapsulation d'une fonction mathématique pour affichage dans l'éditeur de fonctions
 */
export class FunctionObj {

  public static readonly KIND_MATH: string = 'MATH';
  public static readonly KIND_TEXT: string = 'TEXT';
  public static readonly KIND_LOGICAL: string = 'LOGICAL';
  public static readonly KIND_DATE: string = 'DATE';
  public static readonly KIND_COLLECTION: string = 'COLLECTION';
  public static readonly KIND_REF: string = 'REF';
  // public static readonly KIND_TOOL: string = 'TOOL';

  /** construction. En cas d'unlimited, utilisation du parameter[0] pour obtenir le type */
  /**
   *
   * @param code code de la fonction, voir FunctionCoreEngine
   * @param kind famille de fonction
   * @param parameters paramètres de la fonction
   * @param unlimited fonction sans fin (sum, avg...)
   * @param alternateParameters config alternative, par exemple SPLIT(MEASURE;Number)
   */
  public constructor(
    model: Model,
    kind: string,
    code: string,
    parameters: string[],
    unlimited: boolean = false,
    alternateParameters: string[] = null
  ) {
    this._model = model;
    this.kind = kind;
    this.code = code;
    this.parameters = parameters;
    this.unlimitedParameters = unlimited;
    if (this.unlimitedParameters) {
      this.unlimitedParametersKind = parameters[0];
    }
    this.alternateParameters = alternateParameters;
  }

  /** Le modèle pour l'accés au parentVO */
  private _model: Model;

  public kind: string; // type de fonction
  public code: string; // le code de la fonction

  public getCodeAndLabel(): string {
    const result: string = this.getLabel() + ' (' + this.code + ')';
    return result;
  }

  public getLabel(): string {
    return this._model.localeEngine.translateKey('$.function.' + this.code + '.$');
  }

  public getDescription(): string {
    return this._model.localeEngine.translateKey('$.function.desc.' + this.code + '.$');
  }

  public unlimitedParameters: boolean = false; // une collection de params, non limitée
  public unlimitedParametersKind: string; // le type (unique) des params (constantes : string, number, date...)

  public parameters: string[]; // collection ordonnées des types de paramètres (constantes : string, number, date...)

  public measure: MeasureVO; // la mesure en cours
  public mode: string; // le mode fist en cours
  public contents: string[]; // les élements constitutifs de la fonction (valeurs en cours, sous forme de chaines. Si on cherche les content[], ils sont dans le MM
  public formulaParts: string[]; // la formule reconstituées en morceaux de string
  public currentPart: number; // l'indice de démarrage de la formule en cours
  public mm: MeasMode; // le mm construit depuis les paramètres
  public mmi: MeasModeItem; // le mmi correspondant à la fonction
  public currentPartEnd: number; // la part de fin dans la formule complète

  public alternateParameters: string[]; // collection alternative des types de paramètres (constantes : string, number, date...)

  public isAggregate: boolean = false; // une fonction de collection (type collect)
  public isList: boolean = false; // une fonction de collection "Liste"

  /**
   * ##################################################################
   *      Données en cours de manipulation
   * ##################################################################
   */

  /**
   * alimentation des données de la formule pour alimentation de l'éditeur
   * @param measure la mesure en cours de modification (complète)
   * @param mode le mode FIST
   * @param currentPart l'indice de la formule dans la chaine qui sera reconstituée
   */
  public feedDatas(fei: FeiVO, measure: MeasureVO, mode: string, currentPart?: number): void {
    this.measure = measure;
    this.mode = mode;
    if (currentPart) {
      this.currentPart = currentPart;
    }
    this.buildParts(fei);
  }

  /** alimentation des contents depuis la mesure, et construction des parts */
  private buildParts(fei: FeiVO): void {
    const mm: MeasMode = MeasureCoreEngine.measureModeFromFormula(fei, this.measure.source, this.mode);

    const parts: string[] = []; // les éléments de la formule
    if (mm.form.length > 0 && mm.form.trim()[0] === '=') {
      // ajout du = initial
      parts.push('=');
    }
    if (mm) {
      this.buildPartsRecurse(mm.mmi, parts);
    }
    this.formulaParts = parts;
    this.contents = this.mmi.c.map((item: MeasModeItem) => item.form);
  }

  /**
   * collecte récursives des éléments de la formule pour identification de mmi relatif à la fonction
   * @param mmi le mmi dans la pile
   * @param parts le tableau de parts progressivement constitué
   */
  private buildPartsRecurse(mmi: MeasModeItem, parts: string[]): void {
    let seekEnd: boolean = false;
    if (mmi.fSis) {
      parts.push('(');
    } else if (mmi.fFunc && !mmi.isErr) {
      if (parts.length === this.currentPart) {
        // on a identifié le bon mmi
        this.mmi = mmi;
        seekEnd = true;
      }
      parts.push(mmi.funcCode);
      parts.push('(');
    }

    if (mmi.fTer || (mmi.fFunc && mmi.isErr)) {
      // écriture des terminaux
      parts.push(mmi.form);
    } else {
      let nbParams: number = mmi.c.length;
      mmi.c.forEach((mmic: MeasModeItem) => {
        this.buildPartsRecurse(mmic, parts);
        nbParams--;
        if (mmi.fFunc && nbParams > 0) {
          parts.push(';');
        }
      });
    }

    if (mmi.fSis) {
      parts.push(')');
    } else if (mmi.fFunc && !mmi.isErr) {
      parts.push(')');
      if (seekEnd) {
        this.currentPartEnd = parts.length;
        // console.log('endPart', this.currentPartEnd, parts);
      }
    }
  }

  /**
   * contrôle la cohérence du nombre de paramètres
   * @param nbParams les params en cours
   * @returns true si contrôle OK
   */
  public checkParamsLength(nbParams: number): boolean {
    let result: boolean = false; // par défaut, on considère que ça foire
    if (this.unlimitedParameters && nbParams > 0) {
      // paramètres infinis, et au moins 1
      result = true;
    }
    if (!this.unlimitedParameters) {
      // pas de paramètres infinis
      if (this.parameters.length === nbParams) {
        result = true;
      } else if (!!this.alternateParameters && this.alternateParameters.length === nbParams) {
        result = true;
      }
    }

    return result;
  }
}
